import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { App, Button, Card, Col, Row, Space, Tooltip } from 'antd'
import { brand } from '../../theme/brand'
import { Pill, ProgressBar, SectionCard } from '../../theme/listkit'
import { renderAvatarName } from '../../theme/columns'
import { supabaseClient } from '../../lib/supabaseClient'
import { groupsAdminErrorMessage } from '../groups/adminErrors'
import { categoryLabel } from '../groups/categoryOptions'
import { ideaStatusTone, sourceLabel, type IdeaRow } from './list'

const headStyle: React.CSSProperties = { fontFamily: brand.fDisplay, fontWeight: 700, color: brand.ink }
const labelStyle: React.CSSProperties = {
  fontFamily: brand.fSans,
  fontWeight: 700,
  fontSize: 11,
  letterSpacing: '.09em',
  textTransform: 'uppercase',
  color: brand.gold,
  marginBottom: 4,
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={labelStyle}>{label}</div>
      <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 13.5, color: brand.ink }}>{children}</div>
    </div>
  )
}

interface Pledger {
  user_id: string
  name: string
  class_year: string | null
  created_at: string
}

const PLEDGE_THRESHOLD = 8

/**
 * Group idea detail — the pledger roster and the convert/close actions.
 * "Convert to group" is enabled once the idea has an organizer AND has
 * reached the pledge threshold (8, group_idea_threshold() on the server —
 * mirrored here for the button's enabled state only; the server is the real
 * enforcement). Below that, "Convert anyway" still works via `force: true`,
 * confirmed first.
 */
export function GroupIdeaShow() {
  const { id } = useParams<{ id: string }>()
  const { message, modal } = App.useApp()
  const [idea, setIdea] = useState<IdeaRow | null>(null)
  const [organizerName, setOrganizerName] = useState<string | null>(null)
  const [pledgers, setPledgers] = useState<Pledger[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<null | 'convert' | 'close'>(null)

  const load = useCallback(async () => {
    if (!id) return
    setLoading(true)
    try {
      const { data: row, error } = await supabaseClient.from('group_ideas').select('*').eq('id', id).maybeSingle()
      if (error) throw error
      setIdea(row as IdeaRow | null)

      if (row?.organizer_user_id) {
        const { data: org } = await supabaseClient
          .from('profiles')
          .select('name')
          .eq('user_id', row.organizer_user_id)
          .maybeSingle()
        setOrganizerName(org?.name ?? null)
      } else {
        setOrganizerName(null)
      }

      const { data: pledges } = await supabaseClient
        .from('group_idea_pledges')
        .select('user_id, created_at')
        .eq('idea_id', id)
        .order('created_at', { ascending: true })
      const ids = (pledges ?? []).map((p) => p.user_id)
      const byId = new Map<string, { name: string; class_year: string | null }>()
      if (ids.length > 0) {
        const { data: profiles } = await supabaseClient
          .from('profiles')
          .select('user_id, name, class_year')
          .in('user_id', ids)
        for (const p of profiles ?? []) {
          if (p.user_id) byId.set(p.user_id, { name: p.name ?? 'Unknown member', class_year: p.class_year })
        }
      }
      setPledgers(
        (pledges ?? []).map((p) => ({
          user_id: p.user_id,
          created_at: p.created_at,
          name: byId.get(p.user_id)?.name ?? 'Unknown member',
          class_year: byId.get(p.user_id)?.class_year ?? null,
        })),
      )
    } catch (e) {
      message.error(groupsAdminErrorMessage(e))
    } finally {
      setLoading(false)
    }
  }, [id, message])

  useEffect(() => {
    void load()
  }, [load])

  async function convert(force: boolean) {
    if (!id) return
    setBusy('convert')
    try {
      const { data: groupId, error } = await supabaseClient.rpc('convert_group_idea', {
        p_idea: id,
        p_payload: force ? { force: true } : {},
      })
      if (error) throw error
      message.success('Idea converted — every pledger is now a founding member.')
      void load()
      void groupId
    } catch (e) {
      message.error(groupsAdminErrorMessage(e))
    } finally {
      setBusy(null)
    }
  }

  function confirmConvertAnyway() {
    modal.confirm({
      title: 'Convert anyway?',
      content: `This idea has ${idea?.pledge_count ?? 0} of ${PLEDGE_THRESHOLD} pledges. Converting now creates the group below the usual threshold.`,
      okText: 'Convert anyway',
      okButtonProps: { danger: true },
      onOk: () => convert(true),
    })
  }

  async function closeIdea() {
    if (!id) return
    setBusy('close')
    try {
      const { error } = await supabaseClient.from('group_ideas').update({ status: 'closed' }).eq('id', id)
      if (error) throw error
      message.success('Idea closed.')
      void load()
    } catch (e) {
      message.error(groupsAdminErrorMessage(e))
    } finally {
      setBusy(null)
    }
  }

  const readyToConvert = Boolean(idea?.organizer_user_id) && (idea?.pledge_count ?? 0) >= PLEDGE_THRESHOLD
  const isOpen = idea?.status === 'open'

  return (
    <div style={{ padding: '22px 26px 28px' }}>
      <div
        style={{
          background: `linear-gradient(135deg, ${brand.navy} 0%, ${brand.jaguar} 100%)`,
          borderRadius: 14,
          padding: '22px 24px',
          marginBottom: 18,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 18,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div style={{ fontFamily: brand.fSans, fontWeight: 700, fontSize: 11, letterSpacing: '.09em', textTransform: 'uppercase', color: brand.gold, marginBottom: 6 }}>
            Group idea
          </div>
          <div style={{ fontFamily: brand.fDisplay, fontWeight: 700, fontSize: 24, color: '#fff' }}>{idea?.title ?? '—'}</div>
          <Space size={8} style={{ marginTop: 10 }} wrap>
            {idea && <Pill tone={ideaStatusTone(idea.status)}>{idea.status}</Pill>}
            {idea && <Pill tone="jaguar">{categoryLabel(idea.category)}</Pill>}
            {idea && <Pill tone="neutral">{sourceLabel(idea.source)}</Pill>}
          </Space>
        </div>
        {isOpen && (
          <Space wrap>
            <Tooltip title={readyToConvert ? undefined : 'Needs an organizer and 8 pledges — or use Convert anyway'}>
              <Button
                type="primary"
                data-testid="idea-convert"
                loading={busy === 'convert'}
                disabled={busy !== null || !readyToConvert}
                onClick={() => void convert(false)}
              >
                Convert to group
              </Button>
            </Tooltip>
            {!readyToConvert && (
              <Button data-testid="idea-convert-anyway" disabled={busy !== null || !idea?.organizer_user_id} onClick={confirmConvertAnyway}>
                Convert anyway
              </Button>
            )}
            <Button danger data-testid="idea-close" loading={busy === 'close'} disabled={busy !== null} onClick={() => void closeIdea()}>
              Close idea
            </Button>
          </Space>
        )}
      </div>

      <Card loading={loading} title={<span style={headStyle}>The idea</span>} style={{ marginBottom: 16 }}>
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Field label="Tagline">{idea?.tagline ?? '—'}</Field>
            <Field label="Where">{idea?.is_virtual ? 'Virtual' : idea?.place_label ?? '—'}</Field>
          </Col>
          <Col xs={24} md={12}>
            <Field label="Organizer">
              {idea?.organizer_user_id ? organizerName ?? 'Unknown member' : <span style={{ color: brand.sub, fontStyle: 'italic' }}>None yet</span>}
            </Field>
            <Field label="Pledges">
              <div style={{ marginBottom: 6 }}>{`${idea?.pledge_count ?? 0} of ${PLEDGE_THRESHOLD}`}</div>
              <ProgressBar value={idea?.pledge_count ?? 0} max={PLEDGE_THRESHOLD} />
            </Field>
          </Col>
        </Row>
      </Card>

      <SectionCard title="Pledgers" sub={`${pledgers.length} member${pledgers.length === 1 ? '' : 's'}`}>
        {pledgers.length === 0 ? (
          <div style={{ color: brand.sub, fontFamily: brand.fSans, fontSize: 13 }}>No pledges yet.</div>
        ) : (
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            {pledgers.map((p) => (
              <div key={p.user_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Space size={8}>
                  {renderAvatarName(p.name)}
                  {p.user_id === idea?.organizer_user_id && <Pill tone="gold">Organizer</Pill>}
                  {p.class_year && <Pill tone="neutral">{`’${p.class_year.slice(-2)}`}</Pill>}
                </Space>
                <span style={{ fontFamily: brand.fSans, fontSize: 11.5, color: brand.sub }}>
                  {new Date(p.created_at).toLocaleDateString()}
                </span>
              </div>
            ))}
          </Space>
        )}
      </SectionCard>
    </div>
  )
}
